<template>
  <div v-if="shouldShowModal" class="resubscribe-modal-overlay">
    <div class="resubscribe-modal">
      <div class="modal-header">
        <h2>{{ modalTitle }}</h2>
      </div>

      <div class="modal-body">
        <div class="icon-container">
          <i class="fa fa-lock"></i>
        </div>

        <div class="message-content">
          <p class="status-message">{{ statusMessage }}</p>

          <div class="info-box">
            <p>
              Renewing your subscription restores full access to all features,
              including your existing data, automations, and integrations.
            </p>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="billing-options">
          <button @click="resubscribe('monthly')" :disabled="loadingInterval !== null" class="resubscribe-button">
            <i v-if="loadingInterval === 'monthly'" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-calendar-o"></i>
            Monthly
          </button>
          <button @click="resubscribe('yearly')" :disabled="loadingInterval !== null || yearlyDiscountPercentage === null" class="resubscribe-button">
            <i v-if="loadingInterval === 'yearly'" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-calendar-check-o"></i>
            Yearly<span v-if="yearlyDiscountPercentage !== null"> ({{ yearlyDiscountPercentage }}% discount)</span>
          </button>
        </div>
        <p class="contact-line">
          Need help? <a href="mailto:support@bookedsolid.co.uk">Contact support</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { createPayment, getSubscriptionOptions } from '@/app/admin-routes';

export default {
  name: 'ResubscribeModal',
  props: {
    isLapsed: {
      type: Boolean,
      required: true,
    },
    mobile: {
      type: Boolean,
      required: true,
    },
    routeName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loadingInterval: null,
      yearlyDiscountPercentage: null,
    };
  },
  computed: {
    shouldShowModal() {
      if (this.mobile || this.routeName === 'login') return false;
      return this.isLapsed;
    },
    modalTitle() {
      return 'Subscription Inactive';
    },
    statusMessage() {
      return 'Your BookedSolid subscription is no longer active. All features are currently paused until your subscription is renewed.';
    },
  },
  methods: {
    async loadSubscriptionOptions() {
      const options = await getSubscriptionOptions();
      if (options?.success) {
        this.yearlyDiscountPercentage = options.yearlyDiscountPercentage;
      }
    },
    async resubscribe(billingInterval) {
      this.loadingInterval = billingInterval;
      await createPayment(billingInterval, true);
      this.loadingInterval = null;
    },
  },
  mounted() {
    if (this.shouldShowModal) this.loadSubscriptionOptions();
  },
  watch: {
    shouldShowModal(isVisible) {
      if (isVisible && this.yearlyDiscountPercentage === null) {
        this.loadSubscriptionOptions();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.resubscribe-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.92);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.resubscribe-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  border-top: 4px solid #ef4444;
}

.modal-header {
  padding: 28px 28px 0;

  h2 {
    margin: 0;
    color: #1f2937;
    font-size: 1.5rem;
    font-weight: 600;
  }
}

.modal-body {
  padding: 24px 28px;
  text-align: center;

  .icon-container {
    margin-bottom: 20px;

    i {
      font-size: 3rem;
      color: #ef4444;
    }
  }

  .message-content {
    .status-message {
      font-size: 1.05rem;
      line-height: 1.65;
      color: #374151;
      margin-bottom: 20px;
    }

    .info-box {
      background: #f5f7fb;
      border-radius: 10px;
      padding: 16px 20px;
      text-align: left;

      p {
        margin: 0;
        font-size: 0.95rem;
        line-height: 1.6;
        color: #6b7280;
      }
    }
  }
}

.modal-footer {
  padding: 0 28px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  .billing-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
  }

  .resubscribe-button {
    width: 100%;
    padding: 13px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
    color: white;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($colourP, 0.35);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .contact-line {
    margin: 0;
    font-size: 0.875rem;
    color: #9ca3af;

    a {
      color: $colourP;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

@media (max-width: 600px) {
  .resubscribe-modal {
    margin: 20px;
    width: calc(100% - 40px);
  }

  .modal-header {
    padding: 22px 20px 0;

    h2 {
      font-size: 1.3rem;
    }
  }

  .modal-body {
    padding: 20px;

    .icon-container i {
      font-size: 2.5rem;
    }
  }

  .modal-footer {
    padding: 0 20px 22px;
  }

  .modal-footer .billing-options {
    grid-template-columns: 1fr;
  }
}
</style>
